import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../../services/config/api';

function InternalAuditSignup() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const inviteToken = searchParams.get('token');

    const [form, setForm] = useState({ password: '', confirm_password: '' });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!inviteToken) {
            setError('Invalid or missing invite link. Please contact your administrator.');
        }
    }, [inviteToken]);

    const handleRegister = async () => {
        if (!form.password) { setError('Password is required.'); return; }
        if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
        if (form.password !== form.confirm_password) { setError('Passwords do not match.'); return; }
        setLoading(true); setError('');
        try {
            const axios = (await import('axios')).default;
            const res = await axios.post(
                API.AUDIT_PORTAL_REGISTER,
                { password: form.password, token: inviteToken },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Basic YmFzZWFkbWluOmRlZmF1bHRANDU2',
                        'accept': 'application/json',
                    },
                }
            );
            const data = res?.data?.response_data?.data;
            if (data?.email || res?.data?.response_data?.message) {
                setDone(true);
                setMessage(
                    `Account activated${data?.email ? ` for ${data.email}` : ''}. You can now log in.`
                );
            } else {
                setError(res?.data?.response_data?.message || 'Registration failed. Please try again.');
            }
        } catch (e) {
            const errMsg =
                e?.response?.data?.response_data?.message ||
                e?.response?.data?.error?.message ||
                e?.response?.data?.detail || '';
            setError(errMsg || 'Registration failed. The invite link may be expired or already used.');
        }
        setLoading(false);
    };

    const EyeIcon = ({ visible }) => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {visible ? (
                <>
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                </>
            ) : (
                <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                </>
            )}
        </svg>
    );

    const inputStyle = {
        width: '100%', padding: '11px 14px',
        background: '#f8fafc', border: '1px solid #dde3ed',
        borderRadius: 8, color: '#1a2332', fontSize: 13.5,
        outline: 'none', boxSizing: 'border-box',
        transition: 'border-color 0.15s, box-shadow 0.15s',
    };
    const inputFocus = e => { e.target.style.borderColor = '#1b2a4a'; e.target.style.boxShadow = '0 0 0 3px rgba(27,42,74,0.08)'; };
    const inputBlur = e => { e.target.style.borderColor = '#dde3ed'; e.target.style.boxShadow = 'none'; };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}>
            {/* ── Left branding panel ── */}
            <div style={{
                flex: '0 0 45%',
                background: '#0f1f3d',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px 48px',
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Decorative circles */}
                <div style={{ position: 'absolute', top: -100, right: -100, width: 360, height: 360, borderRadius: '50%', background: 'rgba(255,255,255,0.025)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: -80, left: -80, width: 280, height: 280, borderRadius: '50%', background: 'rgba(199,0,23,0.06)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: '55%', right: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.02)', pointerEvents: 'none' }} />

                {/* Logo */}
                <div style={{ marginBottom: 40, textAlign: 'center' }}>
                    <img
                        src="/assets/small_logo.svg"
                        alt="NMT"
                        style={{ height: 72, marginBottom: 18, filter: 'brightness(1.1)' }}
                        onError={e => { e.target.style.display = 'none'; }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <span style={{ fontSize: 20, fontWeight: 900, color: '#C70017', letterSpacing: '3px' }}>NMT</span>
                        <span style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.18)' }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.5)', letterSpacing: '2px', textTransform: 'uppercase' }}>Security</span>
                    </div>
                </div>

                {/* Heading */}
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <div style={{ fontSize: 36, fontWeight: 800, color: '#ffffff', lineHeight: 1.15, letterSpacing: '-0.5px' }}>
                        Internal Audit
                    </div>
                    <div style={{ fontSize: 36, fontWeight: 800, color: '#C70017', lineHeight: 1.15, marginBottom: 18, letterSpacing: '-0.5px' }}>
                        Portal
                    </div>
                    <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', maxWidth: 300, lineHeight: 1.7, margin: '0 auto' }}>
                        You've been invited to join the Internal Audit Portal. Set your password to get started.
                    </div>
                </div>

                {/* Steps */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 13, width: '100%', maxWidth: 310 }}>
                    {[
                        { step: '1', text: 'Set a strong password (min. 8 characters)', active: !done },
                        { step: '2', text: 'Log in with your company email', active: done },
                        { step: '3', text: 'Access your assigned audits & tasks', active: false },
                    ].map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                            <span style={{
                                flexShrink: 0, width: 20, height: 20, borderRadius: '50%',
                                background: item.active ? 'rgba(199,0,23,0.25)' : 'rgba(255,255,255,0.07)',
                                border: `1px solid ${item.active ? 'rgba(199,0,23,0.5)' : 'rgba(255,255,255,0.12)'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, color: item.active ? '#ff6b7a' : 'rgba(255,255,255,0.3)',
                                fontWeight: 700, marginTop: 1,
                            }}>{item.step}</span>
                            <span style={{ fontSize: 13, color: item.active ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.3)', lineHeight: 1.55 }}>
                                {item.text}
                            </span>
                        </div>
                    ))}
                </div>

                <div style={{ position: 'absolute', bottom: 24, fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.5px' }}>
                    Powered by NOVA AI Platform
                </div>
            </div>

            {/* ── Right register panel ── */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 24px',
                background: '#f0f4f8',
            }}>
                <div style={{
                    width: '100%',
                    maxWidth: 430,
                    background: '#ffffff',
                    border: '1px solid #dde3ed',
                    borderRadius: 12,
                    padding: '40px 36px',
                    boxShadow: '0 4px 28px rgba(15,31,61,0.09)',
                }}>
                    {/* Header */}
                    <div style={{ marginBottom: 28 }}>
                        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#0f1f3d', margin: '0 0 8px 0', letterSpacing: '-0.3px' }}>
                            {done ? 'You\'re all set!' : 'Set Your Password'}
                        </h2>
                        <p style={{ fontSize: 13.5, color: '#8492a6', margin: 0, lineHeight: 1.55 }}>
                            {done
                                ? 'Your account is ready. Log in to access the Audit Portal.'
                                : 'Create a password to activate your audit portal account.'}
                        </p>
                    </div>

                    {/* Alerts */}
                    {message && (
                        <div style={{ padding: '10px 14px', borderRadius: 7, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', fontSize: 12.5, marginBottom: 18, lineHeight: 1.5 }}>
                            {message}
                        </div>
                    )}
                    {error && (
                        <div style={{ padding: '10px 14px', borderRadius: 7, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 12.5, marginBottom: 18, lineHeight: 1.5 }}>
                            {error}
                        </div>
                    )}

                    {/* Success state — go to login */}
                    {done ? (
                        <button
                            onClick={() => navigate('/audit-portal/login')}
                            style={{
                                width: '100%', padding: '13px 16px',
                                background: '#0f1f3d', border: 'none', borderRadius: 8,
                                color: '#fff', fontSize: 14.5, fontWeight: 600,
                                cursor: 'pointer', letterSpacing: '0.2px',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => { e.target.style.background = '#1b2a4a'; }}
                            onMouseLeave={e => { e.target.style.background = '#0f1f3d'; }}
                        >
                            Go to Login
                        </button>
                    ) : (
                        <div>
                            {/* New Password */}
                            <div style={{ marginBottom: 18 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>
                                    New Password
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={form.password}
                                        onChange={e => setForm({ ...form, password: e.target.value })}
                                        placeholder="Minimum 8 characters"
                                        style={{ ...inputStyle, paddingRight: 44 }}
                                        onFocus={inputFocus}
                                        onBlur={inputBlur}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(v => !v)}
                                        style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex', alignItems: 'center' }}
                                    >
                                        <EyeIcon visible={showPassword} />
                                    </button>
                                </div>

                                {/* Password strength hint */}
                                {form.password.length > 0 && (
                                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {[1, 2, 3, 4].map(n => {
                                            const strength = Math.min(4, Math.floor(form.password.length / 3));
                                            return (
                                                <div key={n} style={{
                                                    flex: 1, height: 3, borderRadius: 2,
                                                    background: n <= strength
                                                        ? strength <= 1 ? '#ef4444' : strength <= 2 ? '#f97316' : strength <= 3 ? '#eab308' : '#22c55e'
                                                        : '#e2e8f0',
                                                    transition: 'background 0.2s',
                                                }} />
                                            );
                                        })}
                                        <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>
                                            {form.password.length < 4 ? 'Weak' : form.password.length < 7 ? 'Fair' : form.password.length < 10 ? 'Good' : 'Strong'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Confirm Password */}
                            <div style={{ marginBottom: 28 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>
                                    Confirm Password
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={form.confirm_password}
                                        onChange={e => setForm({ ...form, confirm_password: e.target.value })}
                                        onKeyDown={e => e.key === 'Enter' && handleRegister()}
                                        placeholder="Re-enter your password"
                                        style={{
                                            ...inputStyle, paddingRight: 44,
                                            borderColor: form.confirm_password && form.confirm_password !== form.password ? '#fca5a5' : '#dde3ed',
                                        }}
                                        onFocus={inputFocus}
                                        onBlur={inputBlur}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(v => !v)}
                                        style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex', alignItems: 'center' }}
                                    >
                                        <EyeIcon visible={showConfirmPassword} />
                                    </button>
                                </div>
                                {form.confirm_password && form.confirm_password !== form.password && (
                                    <p style={{ fontSize: 11.5, color: '#ef4444', margin: '5px 0 0 0' }}>Passwords do not match</p>
                                )}
                            </div>

                            {/* Submit */}
                            <button
                                onClick={handleRegister}
                                disabled={loading || !inviteToken}
                                style={{
                                    width: '100%', padding: '13px 16px',
                                    background: loading || !inviteToken ? '#8492a6' : '#0f1f3d',
                                    border: 'none', borderRadius: 8,
                                    color: '#fff', fontSize: 14.5, fontWeight: 600,
                                    cursor: loading || !inviteToken ? 'not-allowed' : 'pointer',
                                    letterSpacing: '0.2px',
                                    transition: 'background 0.15s, transform 0.1s',
                                }}
                                onMouseEnter={e => { if (!loading && inviteToken) e.target.style.background = '#1b2a4a'; }}
                                onMouseLeave={e => { if (!loading && inviteToken) e.target.style.background = '#0f1f3d'; }}
                                onMouseDown={e => { e.target.style.transform = 'scale(0.99)'; }}
                                onMouseUp={e => { e.target.style.transform = 'scale(1)'; }}
                            >
                                {loading ? 'Activating account...' : 'Set Password & Continue'}
                            </button>

                            {/* Back to login */}
                            <div style={{ textAlign: 'center', marginTop: 18 }}>
                                <span style={{ fontSize: 13, color: '#8492a6' }}>
                                    Already have an account?{' '}
                                    <span
                                        onClick={() => navigate('/audit-portal/login')}
                                        style={{ color: '#1b6cb0', cursor: 'pointer', fontWeight: 500 }}
                                    >
                                        Log in
                                    </span>
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Footer */}
                    <div style={{ textAlign: 'center', marginTop: 28, paddingTop: 20, borderTop: '1px solid #eef0f5' }}>
                        <span style={{ fontSize: 12, color: '#aab4c4' }}>
                            NMT Security &nbsp;·&nbsp; Internal Audit Portal
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InternalAuditSignup;
